import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  MdArrowBack,
  MdQuiz,
  MdSave,
  MdSearch,
  MdVisibility,
  MdEdit,
} from 'react-icons/md';
import api from '../services/api';
import toast from 'react-hot-toast';
import './Assessment.css';

const MANUAL_TYPES = new Set(['text_input', 'essay', 'case_study']);

const TYPE_LABELS = {
  single_select: 'Single Select',
  mcq: 'Multiple Select',
  essay: 'Essay / Writing',
  case_study: 'Case Study',
  text_input: 'Text',
};

const STATUS_LABEL = {
  assigned: 'Not started',
  in_progress: 'In progress',
  submitted: 'Submitted',
  evaluated: 'Evaluated',
};

const AssessmentReview = () => {
  const { id, attemptId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [editable, setEditable] = useState(false);
  const [selected, setSelected] = useState(null);
  const [grades, setGrades] = useState({});
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const openAttempt = (attempt) => {
    if (!attempt) {
      setSelected(null);
      setGrades({});
      return;
    }
    setSelected(attempt);
    const map = {};
    (attempt.answers || []).forEach((a) => {
      if (a.manualMarks != null) map[String(a.questionId)] = a.manualMarks;
    });
    setGrades(map);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/assessments/${id}?as=creator`);
      if (data.role !== 'creator') {
        toast.error(
          'Only the assessment creator or Super Admin can review this page',
        );
        setAssessment(null);
        return;
      }
      setAssessment(data.data);
      const list = data.attempts || [];
      setAttempts(list);
      setEditable(Boolean(data.editable));

      if (attemptId) {
        const match = list.find((a) => String(a._id) === String(attemptId));
        if (!match) {
          toast.error('Attempt not found');
          navigate(`/assessments/${id}/review`, { replace: true });
          return;
        }
        if (!['submitted', 'evaluated'].includes(match.status)) {
          toast.error('This attempt has not been submitted yet');
          navigate(`/assessments/${id}/review`, { replace: true });
          return;
        }
        openAttempt(match);
      } else {
        openAttempt(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load');
      setAssessment(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, attemptId]);

  const handleSaveGrades = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const payload = Object.entries(grades).map(([questionId, manualMarks]) => ({
        questionId,
        manualMarks: Number(manualMarks),
      }));
      const { data } = await api.post(
        `/assessments/${id}/attempts/${selected._id}/grade`,
        { grades: payload },
      );
      toast.success(data.message || 'Grades saved');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save grades');
    } finally {
      setSaving(false);
    }
  };

  const filteredAttempts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return attempts.filter((att) => {
      if (statusFilter && att.status !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        att.staff?.name,
        att.staff?.email,
        att.attendeeEmail,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [attempts, search, statusFilter]);

  const hasActiveFilters = Boolean(search.trim()) || Boolean(statusFilter);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
  };

  if (loading) {
    return (
      <div className="assessment-page">
        <div className="assessment-loading">
          <span className="spinner" /> Loading…
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="assessment-page">
        <div className="assessment-empty">
          Assessment not found or you are not the creator.{' '}
          <Link to="/assessments/created">Back</Link>
        </div>
      </div>
    );
  }

  const questionById = Object.fromEntries(
    (assessment.questions || []).map((q) => [String(q._id), q]),
  );

  /* ── Attempt detail / grade page ── */
  if (attemptId && selected) {
    const questions = assessment.questions || [];
    const answerByQ = Object.fromEntries(
      (selected.answers || []).map((a) => [String(a.questionId), a]),
    );
    const pct = assessment.totalMarks
      ? Math.round(
          (Number(selected.totalScore || 0) / assessment.totalMarks) * 100,
        )
      : 0;
    const hasManual = questions.some((q) => MANUAL_TYPES.has(q.type));

    return (
      <div className="assessment-result-page">
        <div className="assessment-result-layout">
          <aside className="assessment-result-sidebar">
            <header className="assessment-result-hero">
              <div className="assessment-result-hero-top">
                <div className="assessment-result-hero-copy">
                  <div className="assessment-result-status-row">
                    <span className="assessment-result-kicker">
                      Review attempt
                    </span>
                    <span
                      className={`assessment-result-status-chip ${
                        selected.status === 'evaluated' ? 'done' : 'pending'
                      }`}
                    >
                      {selected.status === 'evaluated'
                        ? 'Fully evaluated'
                        : 'Needs grading'}
                    </span>
                  </div>
                  <h1>
                    <span className="assessment-result-title-icon">
                      <MdQuiz size={22} />
                    </span>
                    {assessment.title}
                  </h1>
                  <p className="assessment-result-meta">
                    {selected.staff?.name ||
                      selected.attendeeEmail ||
                      'Attendee'}
                    {selected.staff?.email
                      ? ` · ${selected.staff.email}`
                      : selected.attendeeEmail && selected.staff?.name
                        ? ` · ${selected.attendeeEmail}`
                        : ''}
                    {selected.submittedAt
                      ? ` · Submitted ${new Date(selected.submittedAt).toLocaleString()}`
                      : ''}
                  </p>
                </div>
                <div
                  className="assessment-result-score-circle"
                  style={{ '--pct': `${Math.min(100, Math.max(0, pct))}` }}
                >
                  <div className="assessment-result-score-circle-inner">
                    <strong>{selected.totalScore ?? 0}</strong>
                    <span>of {assessment.totalMarks}</span>
                    <em>{pct}%</em>
                  </div>
                </div>
              </div>

              <div className="assessment-result-stats">
                <div>
                  <span>Auto score</span>
                  <strong>{selected.autoScore ?? 0}</strong>
                </div>
                <div>
                  <span>Manual score</span>
                  <strong>{selected.manualScore ?? 0}</strong>
                </div>
                <div>
                  <span>Questions</span>
                  <strong>{questions.length}</strong>
                </div>
                <div>
                  <span>Status</span>
                  <strong style={{ textTransform: 'capitalize' }}>
                    {selected.status}
                  </strong>
                </div>
              </div>
            </header>

            <div className="assessment-result-actions assessment-result-actions--sidebar">
              <Link
                to={`/assessments/${id}/review`}
                className="assessment-btn secondary"
              >
                <MdArrowBack size={18} />
                Back to attempts
              </Link>
              {hasManual && (
                <button
                  type="button"
                  className="assessment-btn"
                  disabled={saving}
                  onClick={handleSaveGrades}
                >
                  <MdSave size={18} />
                  {saving ? 'Saving…' : 'Save grades'}
                </button>
              )}
            </div>
          </aside>

          <main className="assessment-result-main">
            <section className="assessment-result-list">
              <div className="assessment-result-list-head">
                <h2>Question review</h2>
                <span>{questions.length} questions</span>
              </div>
              {questions.map((q, idx) => {
                const ans = answerByQ[String(q._id)] || {};
                const isManual = MANUAL_TYPES.has(q.type);
                const selectedIds = (ans.selectedOptionIds || []).map(String);
                const correctIds = (q.options || [])
                  .filter((o) => o.isCorrect)
                  .map((o) => String(o._id));
                const isCorrect =
                  !isManual &&
                  correctIds.length > 0 &&
                  correctIds.length === selectedIds.length &&
                  correctIds.every((cid) => selectedIds.includes(cid));
                const marksGot = !isManual
                  ? Number(ans.autoMarks || 0)
                  : ans.isManuallyGraded
                    ? Number(ans.manualMarks || 0)
                    : null;

                return (
                  <article
                    key={q._id}
                    className={`assessment-result-q ${
                      isManual ? 'manual' : isCorrect ? 'correct' : 'wrong'
                    }`}
                    style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}
                  >
                    <div className="assessment-result-q-index">{idx + 1}</div>
                    <div className="assessment-result-q-body">
                      <div className="assessment-result-q-head">
                        <div>
                          <div className="assessment-result-q-badges">
                            <span className="assessment-exam-pill">
                              {TYPE_LABELS[q.type] || q.type}
                            </span>
                            <span className="assessment-exam-pill marks">
                              {marksGot == null
                                ? `Pending / ${q.marks}`
                                : `${marksGot} / ${q.marks}`}
                            </span>
                          </div>
                          <h3>{q.text}</h3>
                        </div>
                      </div>

                      {!isManual ? (
                        <div className="assessment-result-options">
                          {(q.options || []).map((o, oi) => {
                            const oid = String(o._id);
                            const letter = String.fromCharCode(65 + oi);
                            const userPicked = selectedIds.includes(oid);
                            const isRight = Boolean(o.isCorrect);
                            return (
                              <div
                                key={oid}
                                className={[
                                  'assessment-result-option',
                                  isRight ? 'is-correct' : '',
                                  userPicked && !isRight
                                    ? 'is-wrong-pick'
                                    : '',
                                  userPicked && isRight
                                    ? 'is-right-pick'
                                    : '',
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                              >
                                <span className="assessment-exam-letter">
                                  {letter}
                                </span>
                                <span className="assessment-result-option-text">
                                  {o.text}
                                </span>
                                <span className="assessment-result-option-tags">
                                  {userPicked && <em>Their answer</em>}
                                  {isRight && (
                                    <em className="right">Correct</em>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <>
                          <div className="assessment-result-essay">
                            <p className="assessment-result-essay-label">
                              Staff answer
                            </p>
                            <div className="assessment-result-essay-body">
                              {ans.textAnswer?.trim()
                                ? ans.textAnswer
                                : '(No answer submitted)'}
                            </div>
                          </div>
                          <div className="assessment-manual-box">
                            <label>Award marks (0–{q.marks})</label>
                            <input
                              type="number"
                              min={0}
                              max={q.marks}
                              value={
                                grades[String(q._id)] ??
                                (ans.manualMarks != null
                                  ? ans.manualMarks
                                  : '')
                              }
                              onChange={(e) =>
                                setGrades((g) => ({
                                  ...g,
                                  [String(q._id)]: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </article>
                );
              })}
            </section>
          </main>
        </div>
      </div>
    );
  }

  /* ── Attempts list ── */
  return (
    <div className="assessment-page assessment-page-wide">
      <div className="assessment-header">
        <div>
          <h1>
            <MdQuiz size={28} />
            {assessment.title}
          </h1>
          <p className="assessment-subtitle">
            {assessment.scheduledAt
              ? `${new Date(assessment.scheduledAt).toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })} · `
              : ''}
            {assessment.durationMinutes} min · {assessment.totalMarks} marks ·{' '}
            {(assessment.assignedTo || []).length} assignees
          </p>
        </div>
        <div className="assessment-actions">
          {editable && (
            <Link
              to={`/assessments/${id}/edit`}
              className="assessment-btn secondary"
            >
              <MdEdit size={18} />
              Edit
            </Link>
          )}
          <Link to="/assessments/created" className="assessment-btn secondary">
            Back
          </Link>
        </div>
      </div>

      <h2
        className="assessment-section-title"
        style={{ display: 'block', marginTop: 0 }}
      >
        <span style={{ fontSize: '1.15rem', fontWeight: 700 }}>Attempts</span>
      </h2>

      {attempts.length > 0 && (
        <>
          <div className="assessment-filters-bar">
            <div className="assessment-search-wrap">
              <MdSearch size={20} />
              <input
                type="search"
                placeholder="Search staff name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search attempts"
              />
            </div>
            <select
              className="form-control"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              <option value="assigned">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="submitted">Submitted</option>
              <option value="evaluated">Evaluated</option>
            </select>
            {hasActiveFilters && (
              <button
                type="button"
                className="assessment-btn secondary"
                onClick={clearFilters}
              >
                Clear
              </button>
            )}
          </div>
          <p className="assessment-filter-meta">
            Showing {filteredAttempts.length} of {attempts.length} attempt
            {attempts.length === 1 ? '' : 's'}
          </p>
        </>
      )}

      {attempts.length === 0 ? (
        <div className="assessment-empty">No attempts yet.</div>
      ) : filteredAttempts.length === 0 ? (
        <div className="assessment-empty">
          No attempts match your search or filters.{' '}
          <button
            type="button"
            className="assessment-btn secondary"
            onClick={clearFilters}
            style={{ marginLeft: 8 }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="card assessment-table-card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Staff</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Auto</th>
                  <th>Manual</th>
                  <th>Score</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAttempts.map((att) => {
                  const canOpen = ['submitted', 'evaluated'].includes(
                    att.status,
                  );
                  return (
                    <tr key={att._id}>
                      <td>
                        <div className="assessment-table-title">
                          {att.staff?.name ||
                            att.attendeeEmail ||
                            'Attendee'}
                        </div>
                      </td>
                      <td>
                        {att.staff?.email || att.attendeeEmail || '—'}
                      </td>
                      <td>
                        <span className={`assessment-badge ${att.status}`}>
                          {STATUS_LABEL[att.status] || att.status}
                        </span>
                      </td>
                      <td>
                        {canOpen ? (att.autoScore ?? 0) : '—'}
                      </td>
                      <td>
                        {canOpen ? (att.manualScore ?? 0) : '—'}
                      </td>
                      <td>
                        {canOpen
                          ? `${att.totalScore ?? 0}/${assessment.totalMarks}`
                          : '—'}
                      </td>
                      <td>
                        {att.submittedAt
                          ? new Date(att.submittedAt).toLocaleString()
                          : '—'}
                      </td>
                      <td>
                        <div className="assessment-table-actions">
                          <button
                            type="button"
                            className="assessment-btn sm icon-only"
                            title={
                              canOpen ? 'Grade / view' : 'Not submitted yet'
                            }
                            aria-label={
                              canOpen ? 'Grade / view' : 'Not submitted yet'
                            }
                            disabled={!canOpen}
                            onClick={() =>
                              navigate(
                                `/assessments/${id}/review/${att._id}`,
                              )
                            }
                          >
                            <MdVisibility size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentReview;
